import { useEffect, useMemo, useState } from "react";
import { Bike } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { WorkoutProfile } from "@/components/workouts/WorkoutProfile";
import { WorkoutStepEditor } from "@/components/workouts/WorkoutStepEditor";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { downloadWorkoutZwo, fetchWorkoutLibrary, previewWorkoutAdaptation } from "@/api/workoutTemplateApi";
import { editPlannedSession, fetchCurrentActiveTrainingPlan } from "@/api/trainingPlanApi";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";
import { buildWorkoutPrescriptionSections } from "@/lib/workoutPrescriptionFormatters";
import {
  categoryLabel,
  cloneWorkoutSections,
  durationLabel,
  environmentLabel,
  formatWorkoutClock,
  parseFriendlyDuration,
  progressionLabel,
  purposeCopy,
  supportsRequestedDuration,
  updateEditedStep,
  validateEditedSections,
  workoutTotalSeconds,
} from "@/lib/workoutProfile";

const SPORTS = [
  { id: "cycling", label: "Cycling Workouts" },
  { id: "running", label: "Running Workouts" },
];

const LEVELS = ["ANY", "INTRODUCTORY", "FOUNDATIONAL", "DEVELOPING", "ADVANCED", "HIGHLY_TRAINED"];

const fieldClass = "border border-dark-border bg-dark-surface-1 px-3 py-2 text-sm text-dark-text-primary";

const matchesEnvironment = (template, filter) => {
  if (filter === "any") return true;
  const envs = template?.supported_environments || [];
  if (filter === "indoor") {
    return envs.some((item) => item === "INDOOR_TRAINER" || item === "TREADMILL");
  }
  return envs.includes("OUTDOOR");
};

const workDurations = (template) => (template?.main_set || [])
  .filter((step) => step.step_type === "WORK")
  .map((step) => step.duration_seconds);

export const WorkoutsPage = ({
  loadLibrary = fetchWorkoutLibrary,
  previewAdaptation = previewWorkoutAdaptation,
  loadPlan = fetchCurrentActiveTrainingPlan,
  applyToSession = editPlannedSession,
  downloadWorkout = downloadWorkoutZwo,
}) => {
  const [sport, setSport] = useState("cycling");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("any");
  const [durationText, setDurationText] = useState("");
  const [level, setLevel] = useState("ANY");
  const [environment, setEnvironment] = useState("any");
  const [selectedCode, setSelectedCode] = useState("");
  const [customizing, setCustomizing] = useState(false);
  const [customDuration, setCustomDuration] = useState("60 min");
  const [difficulty, setDifficulty] = useState("STANDARD");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [planSessions, setPlanSessions] = useState([]);
  const [chosenSessionId, setChosenSessionId] = useState("");
  const [useMessage, setUseMessage] = useState("");
  const [useError, setUseError] = useState("");
  const [draft, setDraft] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [editorError, setEditorError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setTemplates([]);
    setSelectedCode("");
    setPreview(null);
    loadLibrary(sport)
      .then((rows) => {
        if (!cancelled) setTemplates(rows || []);
      })
      .catch(() => {
        if (!cancelled) setError("The workout library could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sport, loadLibrary]);

  const categories = useMemo(() => {
    const labels = new Set(templates.map((item) => categoryLabel(item)));
    return ["any", ...Array.from(labels).sort()];
  }, [templates]);

  const parsedDuration = parseFriendlyDuration(durationText);
  const requestedFilterMinutes = parsedDuration.minutes ?? null;

  const visible = useMemo(() => templates.filter((item) => {
    const haystack = `${item.name} ${categoryLabel(item)}`.toLowerCase();
    if (search && !haystack.includes(search.trim().toLowerCase())) return false;
    if (category !== "any" && categoryLabel(item) !== category) return false;
    if (requestedFilterMinutes != null && !supportsRequestedDuration(item, requestedFilterMinutes)) return false;
    if (level !== "ANY" && item.progression_level !== level) return false;
    if (!matchesEnvironment(item, environment)) return false;
    return true;
  }), [templates, search, category, requestedFilterMinutes, level, environment]);

  const selected = templates.find((item) => item.workout_code === selectedCode) || null;
  const quickShown = preview?.status === "FEASIBLE" ? {
    ...selected,
    warmup: preview.warmup,
    main_set: preview.main_set,
    cooldown: preview.cooldown,
  } : selected;
  const shown = advanced && draft ? { ...selected, ...draft } : quickShown;
  const sections = buildWorkoutPrescriptionSections(shown);

  const openWorkout = (item, customize) => {
    setSelectedCode(item.workout_code);
    setCustomizing(customize);
    setPreview(null);
    setPreviewError("");
    setPlanSessions([]);
    setChosenSessionId("");
    setUseMessage("");
    setUseError("");
    const libraryMinutes = parseFriendlyDuration(durationText).minutes;
    const min = item.duration?.minutes_min;
    const max = item.duration?.minutes_max;
    const midpoint = min != null && max != null ? Math.round((min + max) / 2) : 60;
    setCustomDuration(libraryMinutes != null ? durationText.trim() : `${midpoint} min`);
    setDifficulty("STANDARD");
    setDraft(cloneWorkoutSections(item));
    setAdvanced(false);
    setEditorError("");
  };

  const editStep = (path, patch) => {
    setDraft((current) => updateEditedStep(current || cloneWorkoutSections(selected), path, patch));
    setAdvanced(true);
    setEditorError("");
  };

  const exportSections = () => {
    if (advanced && draft) return draft;
    if (preview?.status === "FEASIBLE") {
      return { warmup: preview.warmup, main_set: preview.main_set, cooldown: preview.cooldown };
    }
    return cloneWorkoutSections(selected);
  };

  const downloadCurrent = async () => {
    if (!selected) return;
    if (sport !== "cycling") {
      setEditorError("Download is not available for running yet. A supported running file is still pending.");
      return;
    }
    const sectionsToSave = exportSections();
    const problems = validateEditedSections(sectionsToSave, sport);
    if (problems.length) {
      setEditorError(problems[0]);
      return;
    }
    const file = await downloadWorkout({
      name: selected.name,
      sport,
      ...sectionsToSave,
    });
    if (file?.blob) {
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename || "workout.zwo";
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const closeWorkout = () => {
    setSelectedCode("");
    setCustomizing(false);
  };

  const runPreview = async () => {
    if (!selected) return;
    setPreviewError("");
    setPreview(null);
    const parsed = parseFriendlyDuration(customDuration);
    if (parsed.error || parsed.minutes == null) {
      setPreviewError(parsed.error || "Enter a duration such as 45 min, 1h 30m, or 3h.");
      return;
    }
    try {
      const result = await previewAdaptation({
        workoutCode: selected.workout_code,
        requestedDurationMinutes: parsed.minutes,
        difficulty,
      });
      setPreview(result);
      setPlanSessions([]);
      setChosenSessionId("");
      setUseMessage("");
      setUseError("");
      if (result?.status !== "FEASIBLE") {
        setPreviewError(result?.reason || result?.adaptation_summary || "That change is not available.");
      } else {
        setDraft({ warmup: result.warmup, main_set: result.main_set, cooldown: result.cooldown });
        setAdvanced(false);
        setEditorError("");
      }
    } catch {
      setPreviewError("The preview could not be created.");
    }
  };

  const usePreview = async () => {
    if (!selected || (!advanced && preview?.status !== "FEASIBLE")) return;
    setUseError("");
    setUseMessage("");
    try {
      const plan = await loadPlan();
      const upcoming = (plan?.weeks || [])
        .flatMap((week) => week.sessions || [])
        .filter((session) => session.sport === sport && session.is_editable && !session.is_completed);
      setPlanSessions(upcoming);
      setChosenSessionId(upcoming[0]?.session_id || "");
      if (!upcoming.length) {
        setUseError(`No upcoming ${sport} rides can take this workout.`);
      }
    } catch (err) {
      setUseError(err?.message || "The planned rides could not be loaded.");
    }
  };

  const confirmUse = async () => {
    if (!selected || !chosenSessionId || (!advanced && preview?.status !== "FEASIBLE")) return;
    setUseError("");
    setUseMessage("");
    try {
      const problems = advanced ? validateEditedSections(draft, sport) : [];
      if (problems.length) {
        setEditorError(problems[0]);
        return;
      }
      await applyToSession(chosenSessionId, {
        durationMinutes: advanced
          ? Math.max(5, Math.ceil(workoutTotalSeconds(draft) / 60))
          : parseFriendlyDuration(customDuration).minutes,
        libraryWorkoutCode: selected.workout_code,
        libraryDifficulty: difficulty,
        libraryCustomSteps: advanced ? draft : undefined,
      });
      notifyActivePlanChanged({ source: "library-workout-use", sessionId: chosenSessionId });
      const chosen = planSessions.find((session) => session.session_id === chosenSessionId);
      setUseMessage(
        `Replaced the ${chosen?.scheduled_date || "selected"} ride. The shared workout was not changed.`,
      );
    } catch (err) {
      setUseError(err?.message || "That workout could not replace the planned ride.");
    }
  };

  return (
    <div data-testid="workouts-page">
      <PageHeader
        eyebrow="Library"
        title="Workouts"
        subtitle="Cycling and running sessions from the shared library."
        icon={Bike}
      />

      <div className="mb-6 flex gap-2" role="tablist" aria-label="Sport">
        {SPORTS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={sport === item.id}
            data-testid={`sport-tab-${item.id}`}
            className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border ${
              sport === item.id
                ? "border-brand-primary text-brand-primary"
                : "border-dark-border text-dark-text-secondary"
            }`}
            onClick={() => setSport(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-5">
        <input
          className={fieldClass}
          placeholder="Search workouts"
          aria-label="Search workouts"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select aria-label="Category" className={fieldClass} value={category} onChange={(event) => setCategory(event.target.value)}>
          {categories.map((item) => (
            <option key={item} value={item}>{item === "any" ? "All categories" : item}</option>
          ))}
        </select>
        <input
          className={fieldClass}
          aria-label="Desired duration"
          placeholder="Duration, e.g. 75 min"
          value={durationText}
          onChange={(event) => setDurationText(event.target.value)}
        />
        <select aria-label="Difficulty" className={fieldClass} value={level} onChange={(event) => setLevel(event.target.value)}>
          {LEVELS.map((item) => (
            <option key={item} value={item}>{item === "ANY" ? "Any difficulty" : progressionLabel(item)}</option>
          ))}
        </select>
        <select aria-label="Setting" className={fieldClass} value={environment} onChange={(event) => setEnvironment(event.target.value)}>
          <option value="any">Indoor or outdoor</option>
          <option value="indoor">Indoor</option>
          <option value="outdoor">Outdoor</option>
        </select>
      </div>

      {parsedDuration.error ? (
        <p className="mb-4 text-sm text-metric-hr" role="alert" data-testid="duration-parse-error">{parsedDuration.error}</p>
      ) : null}
      {requestedFilterMinutes != null && !parsedDuration.error && visible.length < templates.length ? (
        <p className="mb-4 text-sm text-dark-text-secondary" data-testid="duration-filter-note">
          Showing workouts whose supported range includes {requestedFilterMinutes} min. A workout outside that range cannot be adapted to this duration.
        </p>
      ) : null}

      {loading ? <p className="text-sm text-dark-text-secondary">Loading workouts…</p> : null}
      {error ? <p className="text-sm text-metric-hr" role="alert">{error}</p> : null}
      {!loading && !error && visible.length === 0 ? (
        <p className="text-sm text-dark-text-secondary">No workouts match those filters.</p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid={`workout-list-${sport}`}>
        {visible.map((item) => (
          <article key={item.workout_code} className="border border-dark-border bg-dark-surface-1 p-3" data-testid="workout-card">
            <h2 className="font-heading text-base leading-tight">{item.name}</h2>
            <p className="mt-1 text-[10px] tracking-[0.14em] uppercase text-brand-primary">{categoryLabel(item)}</p>
            <p className="mt-1 text-xs text-dark-text-secondary line-clamp-2">{purposeCopy(item)}</p>
            <p className="mt-1 text-xs">{durationLabel(item)} · {progressionLabel(item.progression_level)}</p>
            <div className="mt-2">
              <WorkoutProfile template={item} height={112} testId={`profile-${item.workout_code}`} />
            </div>
            <div className="mt-3 flex gap-2">
              <button type="button" className="border border-dark-border px-3 py-2 text-xs uppercase tracking-[0.14em]" onClick={() => openWorkout(item, false)}>
                View
              </button>
              <button type="button" className="border border-dark-border px-3 py-2 text-xs uppercase tracking-[0.14em]" onClick={() => openWorkout(item, true)}>
                Customize
              </button>
            </div>
          </article>
        ))}
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => { if (!open) closeWorkout(); }}>
        <DialogContent
          className={`max-h-[90vh] overflow-y-auto bg-dark-background text-dark-text-primary ${customizing ? "max-w-5xl" : "max-w-3xl"}`}
          data-testid={customizing ? "workout-customize" : "workout-detail"}
        >
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{selected.name}</DialogTitle>
                <DialogDescription>{purposeCopy(selected)}</DialogDescription>
              </DialogHeader>
              <p className="text-sm">
                {categoryLabel(selected)} · {durationLabel(selected)} · {progressionLabel(selected.progression_level)} · {environmentLabel(selected)}
              </p>
              <p className="text-sm">
                Prescription: {selected.prescription?.preferred_method}
                {(selected.prescription?.supported_fallback_methods || []).length
                  ? `, then ${selected.prescription.supported_fallback_methods.join(", ")}`
                  : ""}
              </p>
              <p className="text-sm">
                Equipment: {(selected.required_equipment || []).join(", ") || "None required"}
                {" · "}
                Sensors: {(selected.required_sensors || []).join(", ") || "None required"}
              </p>
              <p className="text-sm" data-testid="workout-time">Workout Time {formatWorkoutClock(workoutTotalSeconds(exportSections()))}</p>
              <WorkoutProfile template={shown} height={200} testId="workout-detail-profile" />
              <button type="button" className="border border-dark-border px-3 py-2 text-xs uppercase tracking-[0.14em]" data-testid="download-workout" onClick={downloadCurrent}>
                Download Workout
              </button>
              {editorError ? <p className="text-sm text-metric-hr" role="alert" data-testid="editor-error">{editorError}</p> : null}
              {sections.map((section) => (
                <div key={section.key}>
                  <h3 className="text-xs tracking-[0.16em] uppercase text-dark-text-secondary">{section.title}</h3>
                  <ul className="mt-2 space-y-1 text-sm">
                    {section.items.map((item) => (
                      <li key={`${section.key}-${item.headline || item.label}`}>
                        {item.headline || item.label}
                        {item.duration ? ` · ${item.duration}` : ""}
                        {item.intensity ? ` · ${item.intensity}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {customizing ? (
                <div className="border-t border-dark-border pt-4">
                  <p className="text-sm text-dark-text-secondary">
                    Quick Adjust changes length or an existing easier or harder version. Advanced Customize edits the steps. The shared workout stays the same.
                  </p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-sm">
                      Desired duration
                      <input
                        className={`${fieldClass} mt-1 block w-40`}
                        aria-label="Desired duration"
                        value={customDuration}
                        onChange={(event) => setCustomDuration(event.target.value)}
                      />
                    </label>
                    {["EASIER", "STANDARD", "HARDER"].map((item) => (
                      <button
                        key={item}
                        type="button"
                        aria-pressed={difficulty === item}
                        className={`px-3 py-2 text-xs uppercase tracking-[0.14em] border ${
                          difficulty === item ? "border-brand-primary text-brand-primary" : "border-dark-border"
                        }`}
                        onClick={() => setDifficulty(item)}
                      >
                        {progressionLabel(item)}
                      </button>
                    ))}
                    <button type="button" className="bg-brand-primary text-white px-4 py-2 text-xs uppercase tracking-[0.14em]" onClick={runPreview}>
                      Preview Changes
                    </button>
                  </div>
                  {previewError ? (
                    <p className="mt-3 text-sm text-metric-hr" role="alert" data-testid="adaptation-message">{previewError}</p>
                  ) : null}
                  {preview?.status === "FEASIBLE" ? (
                    <p className="mt-3 text-sm" data-testid="adaptation-message">
                      {preview.adaptation_summary} Preview is {Math.round(preview.resolved_duration_seconds / 60)} minutes.
                      Work intervals: {workDurations(preview).join(", ") || "none"} seconds.
                    </p>
                  ) : null}
                  {draft ? <WorkoutStepEditor sections={draft} onChange={editStep} /> : null}
                  {preview?.status === "FEASIBLE" || advanced ? (
                    <button
                      type="button"
                      className="mt-3 bg-brand-primary text-white px-4 py-2 text-xs uppercase tracking-[0.14em]"
                      data-testid="use-customized-workout"
                      onClick={usePreview}
                    >
                      Use This Workout
                    </button>
                  ) : null}
                  {planSessions.length ? (
                    <div className="mt-3" data-testid="planned-session-picker">
                      <label className="text-sm">
                        Upcoming ride
                        <select
                          className={`${fieldClass} mt-1 block`}
                          aria-label="Upcoming ride"
                          value={chosenSessionId}
                          onChange={(event) => setChosenSessionId(event.target.value)}
                        >
                          {planSessions.map((session) => (
                            <option key={session.session_id} value={session.session_id}>
                              {session.scheduled_date} · {session.workout_name || session.workout_code} · {session.duration_minutes} min
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="mt-3 bg-brand-primary text-white px-4 py-2 text-xs uppercase tracking-[0.14em]"
                        data-testid="confirm-use-workout"
                        onClick={confirmUse}
                      >
                        Replace this ride
                      </button>
                    </div>
                  ) : null}
                  {useError ? (
                    <p className="mt-3 text-sm text-metric-hr" role="alert" data-testid="use-workout-message">{useError}</p>
                  ) : null}
                  {useMessage ? (
                    <p className="mt-3 text-sm" data-testid="use-workout-message">{useMessage}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
